import type { AxiosRequestConfig } from "axios";
import { axiosInstance } from "@/lib/axios";

// All backend services now live behind this single proxy base — the old
// platform-manage.nftbaz-api.tech host is stale.
const MANAGE_API_REMOTE = process.env.NEXT_PUBLIC_MANAGE_API_BASE_URL || "https://nftbaz.com/proxy-api/api";
const MANAGE_API_LOCAL_PROXY = "/api/manage-proxy";

function getManageApiBaseUrl() {
  if (typeof window !== "undefined") {
    // Always go through our own same-origin Next.js rewrite in the browser.
    // The manage API's CORS allowlist only covers a fixed set of known
    // origins (platform.nftbaz.com, localhost) — it does NOT include its
    // own bare nftbaz.com domain, so calling MANAGE_API_REMOTE directly
    // from the browser gets CORS-blocked on whatever origin isn't on that
    // list (confirmed: OPTIONS to /drop/create with Origin: nftbaz.com
    // comes back with no Access-Control-Allow-Origin at all). Routing
    // through /api/manage-proxy sidesteps CORS entirely — the browser only
    // ever talks to our own origin, and this Next.js server does the
    // cross-origin fetch itself, which isn't subject to CORS.
    return `${window.location.origin}${MANAGE_API_LOCAL_PROXY}`;
  }
  return MANAGE_API_REMOTE;
}

interface GetDropsParams {
  limit?: number;
  network_id?: string;
  status?: string;
  user_id?: number;
  page?: number;
  from?: number | string;
  to?: number | string;
  /** Name filter — `/drop?name=` matches part of the name (used by search). */
  name?: string;
  /** Bearer token — required for owner-only views (e.g. My Drops) so the
   *  backend returns the caller's own draft/unpublished drops, not just the
   *  publicly listed ones. */
  token?: string | null;
}

export const dropService = {
  // === DROP CRUD ===

  async getDrops(params: GetDropsParams = {}, lang: string = "en"): Promise<any> {
    const query = new URLSearchParams();
    if (params.limit) query.append("limit", params.limit.toString());
    if (params.network_id) query.append("network_id", params.network_id);
    if (params.status) query.append("status", params.status);
    if (params.user_id) query.append("user_id", params.user_id.toString());
    if (params.page) query.append("page", params.page.toString());
    if (params.from) query.append("from", params.from.toString());
    if (params.to) query.append("to", params.to.toString());
    if (params.name) query.append("name", params.name);

    try {
      const base = getManageApiBaseUrl();
      // Goes through axiosInstance (not raw axios) so on the client its
      // request interceptor waits for auth-store hydration and attaches the
      // bearer token itself — see waitForAuthHydration() in lib/axios.ts.
      // That interceptor reads from the client-only Zustand store, which is
      // always empty on the server, so `params.token` (e.g. from a Server
      // Component reading the request's cookies) is still passed through
      // explicitly here — it wins over the interceptor's (empty) lookup
      // without conflicting with it on the client.
      const response = await axiosInstance.get(`${base}/drop?${query.toString()}`, {
        headers: {
          "lang": lang,
          ...(params.token ? { Authorization: `Bearer ${params.token}` } : {}),
        },
      });
      return response.data;
    } catch (error) {
      console.error("getDrops error:", error);
      return { data: { drops: [] }, message: "offline_fallback" };
    }
  },

  /**
   * `getDrop`, with the REASON a lookup came back empty preserved.
   *
   * `getDrop` below answers a missing drop and a dead backend with the same
   * `null`, and the public drop page turns `null` into `notFound()` — so an
   * outage told Google every drop had been deleted. A 404 is a removal
   * signal; that is worse than the empty-200 soft 404 this codebase spent
   * five fixes removing. Callers that only need "the drop or nothing" (the
   * three owner-only edit routes) keep using `getDrop`.
   *
   * Same three-way shape as `fetchArtist` in `features/artists/server.ts`.
   */
  async getDropResult(
    idOrSlug: string,
    token?: string | null
  ): Promise<{ kind: "ok"; data: any } | { kind: "missing" } | { kind: "failed"; status?: number }> {
    try {
      const base = getManageApiBaseUrl();
      const response = await axiosInstance.get(`${base}/drop/${idOrSlug}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      return { kind: "ok", data: response.data };
    } catch (error) {
      // Axios puts the response on the error; `serverFetch` puts the status
      // on it directly. This path is always axios.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 410) return { kind: "missing" };
      console.error("getDropResult error:", error);
      return { kind: "failed", status };
    }
  },

  async getDrop(idOrSlug: string, token?: string | null): Promise<any> {
    try {
      const base = getManageApiBaseUrl();
      // Draft / unpublished drops (the ones you can still add items to) are
      // owner-only, so this fetch must carry the bearer token or it 404s.
      //
      // Using axiosInstance instead of raw axios matters on the client: its
      // request interceptor awaits auth-store hydration before attaching the
      // token, which a plain axios.get() reading `token` at call time does
      // not — on a fresh mount, `token` from useAuthStore() can still be null
      // (skipHydration: true, rehydrated async in ClientLayout), so the very
      // first fetch would go out unauthenticated, 404, and permanently
      // render "Drop not found" for the drop's own owner.
      //
      // The interceptor's token lookup is a client-only Zustand store,
      // though — on the server it's always empty, so a Server Component
      // (e.g. drops/[slug]/page.tsx reading the request's own cookies) still
      // needs to pass `token` through explicitly. Setting the header here
      // covers that case without fighting the interceptor on the client: the
      // interceptor only overwrites `Authorization` when *it* finds a token,
      // so an explicit header set here wins whenever the store has none.
      const response = await axiosInstance.get(`${base}/drop/${idOrSlug}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      return response.data;
    } catch (error) {
      console.error("getDrop error:", error);
      return null;
    }
  },

  async createDrop(payload: any, config?: AxiosRequestConfig): Promise<any> {
    const base = getManageApiBaseUrl();
    const response = await axiosInstance.post(`${base}/drop/create`, payload, config);
    return response.data;
  },

  /**
   * POST, not PUT. Verified against the live route, which answers a GET with
   * `405 Method Not Allowed` and `allow: POST`
   * ("The GET method is not supported for route api/drop/update/1. Supported
   * methods: POST."). The PUT this used to send would have been rejected the
   * same way — the method was never exercised, because nothing in the app
   * called this until the drop edit route existed.
   */
  async updateDrop(id: string | number, payload: any, config?: AxiosRequestConfig): Promise<any> {
    const base = getManageApiBaseUrl();
    const response = await axiosInstance.post(`${base}/drop/update/${id}`, payload, config);
    return response.data;
  },

  async deleteDrop(id: string | number): Promise<any> {
    const base = getManageApiBaseUrl();
    const response = await axiosInstance.delete(`${base}/drop/delete/${id}`);
    return response.data;
  },

  /**
   * Manual expiry. The backend accepts this only while the drop is `accept` or
   * `active`, and it writes the drop's end time — after which no mint is
   * allowed. Callers must gate on `canExpireDrop` before offering it; this
   * method does not re-check, it just sends.
   *
   * POST is CONFIRMED, not assumed: the live route answers a GET with
   * `405 Method Not Allowed` and `allow: POST`
   * ("The GET method is not supported for route api/drop/expire_at/1.
   * Supported methods: POST.").
   *
   * `expiredAt` must already be in the API's `YYYY-MM-DD HH:mm:ss` form
   * (`permissions.toApiDateTime` produces it).
   */
  async expireDrop(id: string | number, expiredAt: string): Promise<any> {
    const base = getManageApiBaseUrl();
    const response = await axiosInstance.post(`${base}/drop/expire_at/${id}`, {
      expired_at: expiredAt,
    });
    return response.data;
  },

  // === DROP ITEM CRUD ===
  // Drop items live on the main platform API (not the manage API), so relative
  // paths go through axiosInstance and its NEXT_PUBLIC_API_BASE_URL base.

  async getDropItems(params: Record<string, any> = {}, lang: string = "en"): Promise<any> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") query.append(key, String(value));
    });
    try {
      const response = await axiosInstance.get(`/drop-item?${query.toString()}`, {
        headers: { "lang": lang },
      });
      return response.data;
    } catch (error) {
      return { data: { items: [] } };
    }
  },

  async getDropItem(slug: string, lang: string = "en"): Promise<any> {
    try {
      const response = await axiosInstance.get(`/drop-item/${slug}`, {
        headers: { "lang": lang },
      });
      return response.data;
    } catch {
      return null;
    }
  },

  async createDropItem(payload: any, config?: AxiosRequestConfig): Promise<any> {
    const response = await axiosInstance.post(`/drop-item/create`, payload, config);
    return response.data;
  },

  async updateDropItem(id: string | number, payload: any, config?: AxiosRequestConfig): Promise<any> {
    const response = await axiosInstance.post(`/drop-item/update/${id}`, payload, config);
    return response.data;
  },

  async deleteDropItem(id: string | number): Promise<any> {
    const response = await axiosInstance.delete(`/drop-item/delete/${id}`);
    return response.data;
  },

  /**
   * Mints one drop item.
   *
   * This replaces `buyDropItem`, which posted to `drop-item/buy/{id}` — a path
   * this API does not expose. The real endpoint is `drop-item/mint/{dropItemId}`
   * and it takes ONLY the drop item id: no quantity, no amount, no currency.
   * Anything a caller wanted to send alongside it (a quantity, a computed
   * total) had nowhere to go, which is why the old quantity-sequencing purchase
   * flow was fiction on top of a single-shot mint.
   */
  async mintDropItem(dropItemId: string | number): Promise<any> {
    const response = await axiosInstance.post(`/drop-item/mint/${dropItemId}`);
    return response.data;
  },
};
